import {
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    MessageCircle,
    Plus,
    Trash2,
    TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    createEnquiry,
    deleteEnquiry,
    fetchEnquiries,
    fetchTransporters,
    saveQuote,
    setEnquiryStatus,
    whatsappLink,
    type Enquiry,
    type EnquiryRecipient,
    type Transporter,
} from './dispatchApi';

const today = () => new Date().toISOString().slice(0, 10);

const NewEnquiryDialog = ({
    open,
    onOpenChange,
    onCreated,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    onCreated: () => void;
}) => {
    const [transporters, setTransporters] = useState<Transporter[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [f, setF] = useState({
        enquiry_date: today(),
        vehicle_length_ft: '',
        loading_location: '',
        unloading_location: '',
        tonnage: '',
        description: '',
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        fetchTransporters()
            .then(t => {
                const active = t.filter(x => x.is_active);
                setTransporters(active);
                setSelected(active.filter(x => x.phone).map(x => x.id));
            })
            .catch(e => toast.error(e.message));
    }, [open]);

    const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }));
    const valid =
        f.loading_location.trim() &&
        f.unloading_location.trim() &&
        Number(f.tonnage) > 0;

    const submit = async () => {
        setBusy(true);
        try {
            const id = await createEnquiry(
                {
                    enquiry_date: f.enquiry_date,
                    vehicle_length_ft: f.vehicle_length_ft
                        ? Number(f.vehicle_length_ft)
                        : null,
                    loading_location: f.loading_location.trim(),
                    unloading_location: f.unloading_location.trim(),
                    tonnage: Number(f.tonnage),
                    description: f.description.trim() || null,
                },
                selected,
            );
            toast.success(`Enquiry #${id} created`);
            setF({
                enquiry_date: today(),
                vehicle_length_ft: '',
                loading_location: '',
                unloading_location: '',
                tonnage: '',
                description: '',
            });
            onOpenChange(false);
            onCreated();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="text-primary">New Enquiry</DialogTitle>
                    <DialogDescription>
                        Request freight quotes from your transporters.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="enq-date">Date</Label>
                        <Input
                            id="enq-date"
                            type="date"
                            value={f.enquiry_date}
                            onChange={e => set('enquiry_date', e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="enq-len">Vehicle length needed (ft)</Label>
                        <Input
                            id="enq-len"
                            type="number"
                            placeholder="e.g. 20"
                            value={f.vehicle_length_ft}
                            onChange={e => set('vehicle_length_ft', e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="enq-from">Loading location</Label>
                        <Input
                            id="enq-from"
                            placeholder="e.g. Pune"
                            value={f.loading_location}
                            onChange={e => set('loading_location', e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="enq-to">Unloading location</Label>
                        <Input
                            id="enq-to"
                            placeholder="e.g. Mumbai"
                            value={f.unloading_location}
                            onChange={e => set('unloading_location', e.target.value)}
                        />
                    </div>
                    <div className="space-y-1 col-span-2">
                        <Label htmlFor="enq-ton">Tonnage / Load (MT)</Label>
                        <Input
                            id="enq-ton"
                            type="number"
                            step="0.01"
                            placeholder="e.g. 4"
                            value={f.tonnage}
                            onChange={e => set('tonnage', e.target.value)}
                        />
                    </div>
                    <div className="space-y-1 col-span-2">
                        <Label htmlFor="enq-desc">Load description (optional)</Label>
                        <Input
                            id="enq-desc"
                            placeholder="e.g. steel clamps, palletised"
                            value={f.description}
                            onChange={e => set('description', e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <Label className="mb-1.5 block">Send enquiry to</Label>
                    <div className="max-h-40 overflow-y-auto rounded-xl border divide-y">
                        {transporters.length === 0 && (
                            <p className="p-3 text-sm text-muted-foreground">
                                No transporters. Add some from the Transporters
                                button first.
                            </p>
                        )}
                        {transporters.map(t => (
                            <label
                                key={t.id}
                                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-primary/5"
                            >
                                <input
                                    type="checkbox"
                                    className="accent-[var(--primary)] size-4"
                                    checked={selected.includes(t.id)}
                                    onChange={e =>
                                        setSelected(s =>
                                            e.target.checked
                                                ? [...s, t.id]
                                                : s.filter(x => x !== t.id),
                                        )
                                    }
                                />
                                <span className="flex-1 text-sm">{t.name}</span>
                                {!t.phone && (
                                    <span className="text-[10px] font-semibold text-amber-600 flex items-center gap-1">
                                        <TriangleAlert className="size-3" /> NO
                                        PHONE
                                    </span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        id="create-enquiry-btn"
                        onClick={submit}
                        disabled={!valid || busy}
                    >
                        Create Enquiry
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const EnquiriesDialog = ({
    open,
    onOpenChange,
    onBook,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    /** Called when the user books a transporter; parent opens a prefilled dispatch entry. */
    onBook: (enquiry: Enquiry, transporter: Transporter, quote: number | null) => void;
}) => {
    const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
    const [recipients, setRecipients] = useState<EnquiryRecipient[]>([]);
    const [transporters, setTransporters] = useState<Transporter[]>([]);
    const [newOpen, setNewOpen] = useState(false);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [quotes, setQuotes] = useState<Record<number, string>>({});

    const load = useCallback(async () => {
        try {
            const [e, t] = await Promise.all([
                fetchEnquiries(),
                fetchTransporters(),
            ]);
            setEnquiries(e.enquiries);
            setRecipients(e.recipients);
            setTransporters(t);
            setQuotes(
                Object.fromEntries(
                    e.recipients.map(r => [r.id, r.quote_amount?.toString() ?? '']),
                ),
            );
        } catch (err: any) {
            toast.error(err.message);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const tById = new Map(transporters.map(t => [t.id, t]));

    const saveQ = async (r: EnquiryRecipient) => {
        const v = quotes[r.id];
        try {
            await saveQuote(r.id, v ? Number(v) : null);
            toast.success('Quote saved');
            load();
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const book = async (enq: Enquiry, r: EnquiryRecipient) => {
        const t = tById.get(r.transporter_id);
        if (!t) return;
        try {
            await setEnquiryStatus(enq.id, 'booked', t.id);
            onBook(enq, t, r.quote_amount);
            load();
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const remove = async (enq: Enquiry) => {
        if (!window.confirm('Delete this enquiry?')) return;
        try {
            await deleteEnquiry(enq.id);
            load();
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl rounded-2xl">
                    <DialogHeader>
                        <div className="flex items-center justify-between pr-6">
                            <DialogTitle className="text-primary">
                                Enquiries
                            </DialogTitle>
                            <Button
                                id="new-enquiry-btn"
                                size="sm"
                                onClick={() => setNewOpen(true)}
                            >
                                <Plus /> New Enquiry
                            </Button>
                        </div>
                        <DialogDescription>
                            Track quotes from transporters and book the best
                            one.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                        {enquiries.length === 0 && (
                            <p className="text-center text-sm text-muted-foreground py-8">
                                No enquiries yet.
                            </p>
                        )}
                        {enquiries.map(enq => {
                            const rs = recipients.filter(
                                r => r.enquiry_id === enq.id,
                            );
                            const quoted = rs.filter(
                                r => r.quote_amount != null,
                            ).length;
                            const isOpen = expanded === enq.id;
                            const lowest = Math.min(
                                ...rs
                                    .filter(r => r.quote_amount != null)
                                    .map(r => r.quote_amount as number),
                            );
                            return (
                                <div
                                    key={enq.id}
                                    className="rounded-xl border bg-card overflow-hidden"
                                >
                                    <button
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-primary/5 transition-colors"
                                        onClick={() =>
                                            setExpanded(isOpen ? null : enq.id)
                                        }
                                    >
                                        <div className="min-w-0">
                                            <div className="text-xs text-muted-foreground">
                                                {enq.enquiry_date}
                                            </div>
                                            <div className="font-semibold truncate">
                                                {enq.loading_location} →{' '}
                                                {enq.unloading_location}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {enq.tonnage} MT ·{' '}
                                                {enq.vehicle_length_ft ?? '—'} ft
                                                · {quoted}/{rs.length} quoted
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                className={
                                                    enq.status === 'open'
                                                        ? 'bg-primary/15 text-primary border-primary/30'
                                                        : 'bg-emerald-600 text-white'
                                                }
                                                variant="outline"
                                            >
                                                {enq.status.toUpperCase()}
                                            </Badge>
                                            {isOpen ? (
                                                <ChevronUp className="size-4" />
                                            ) : (
                                                <ChevronDown className="size-4" />
                                            )}
                                        </div>
                                    </button>

                                    {isOpen && (
                                        <div className="border-t bg-muted/30 px-4 py-3 space-y-2">
                                            {enq.description && (
                                                <p className="text-xs text-muted-foreground">
                                                    {enq.description}
                                                </p>
                                            )}
                                            {rs.map(r => {
                                                const t = tById.get(r.transporter_id);
                                                const wa = whatsappLink(
                                                    t?.phone ?? null,
                                                    `Transport enquiry: ${enq.loading_location} to ${enq.unloading_location}, ${enq.tonnage} MT, ${enq.vehicle_length_ft ?? ''} ft vehicle on ${enq.enquiry_date}. ${enq.description ?? ''} Please share your best rate.`,
                                                );
                                                const best =
                                                    r.quote_amount != null &&
                                                    r.quote_amount === lowest;
                                                return (
                                                    <div
                                                        key={r.id}
                                                        className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2"
                                                    >
                                                        <div className="flex-1 min-w-[8rem]">
                                                            <div className="text-sm font-medium">
                                                                {t?.name ?? 'Unknown'}
                                                            </div>
                                                            {best && (
                                                                <span className="text-[10px] font-semibold text-emerald-600">
                                                                    LOWEST QUOTE
                                                                </span>
                                                            )}
                                                        </div>
                                                        {wa ? (
                                                            <a
                                                                href={wa}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-emerald-600 hover:text-emerald-700"
                                                                title="Send via WhatsApp"
                                                            >
                                                                <MessageCircle className="size-5" />
                                                            </a>
                                                        ) : (
                                                            <span className="text-[10px] text-amber-600">
                                                                no phone
                                                            </span>
                                                        )}
                                                        <Input
                                                            className="w-28 h-8"
                                                            type="number"
                                                            placeholder="Quote ₹"
                                                            value={quotes[r.id] ?? ''}
                                                            onChange={e =>
                                                                setQuotes(q => ({
                                                                    ...q,
                                                                    [r.id]: e.target.value,
                                                                }))
                                                            }
                                                            onBlur={() => {
                                                                if (
                                                                    (quotes[r.id] ?? '') !==
                                                                    (r.quote_amount?.toString() ?? '')
                                                                )
                                                                    saveQ(r);
                                                            }}
                                                        />
                                                        {enq.status === 'open' && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                disabled={r.quote_amount == null}
                                                                onClick={() => book(enq, r)}
                                                            >
                                                                <CheckCircle2 /> Book
                                                            </Button>
                                                        )}
                                                        {enq.booked_transporter_id ===
                                                            r.transporter_id && (
                                                            <Badge className="bg-emerald-600 text-white">
                                                                BOOKED
                                                            </Badge>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <div className="flex justify-end">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-destructive hover:bg-destructive/10"
                                                    onClick={() => remove(enq)}
                                                >
                                                    <Trash2 /> Delete enquiry
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
            <NewEnquiryDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                onCreated={load}
            />
        </>
    );
};
